import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { ExpandMore, PlayArrow, ArrowForward, Close } from "@mui/icons-material";
import AboutUsVideo from "../misc/AboutUsVideo";
import { mergeManagedContent } from "../../hooks/mergeManagedContent";
import { PF } from "../../config";
import {
  colors,
  eyebrowSx,
  fonts,
  radii,
  shadows,
} from "../../theme/designTokens";

export default function Focus() {
  const [viewMoreAbout, setViewMoreAbout] = useState(false);
  const [viewAboutVid, setViewAboutVid] = useState(false);
  const [contentSettings, setContentSettings] = useState([]);
  const [langId, setLangId] = useState("");

  // Store
  const { managedContentSettings } = useSelector(
    (state) => state.managedContentSettings
  );
  const { activeLangFront } = useSelector((state) => state.miscFront);
  const { langs } = useSelector((state) => state.misc);

  // Set Language
  useEffect(() => {
    if (activeLangFront) setLangId(activeLangFront._id);
  }, [activeLangFront]);

  // Set Settings
  useEffect(() => {
    if (
      managedContentSettings &&
      managedContentSettings.length > 0 &&
      langId !== ""
    ) {
      const M = mergeManagedContent(managedContentSettings, langId, langs);
      if (M) setContentSettings(M);
    }
  }, [langId, managedContentSettings]);

  //! Translate
  // Store
  const { langArrFromClientSideFrontend } = useSelector(
    (state) => state.miscFront
  );
  const translateFront = (String) => {
    let translation = "";
    if (
      langArrFromClientSideFrontend &&
      langArrFromClientSideFrontend.length > 0
    ) {
      const T = langArrFromClientSideFrontend.find((l) => l.title === String);
      if (T && T.value) {
        let obj = { ...T };
        if (T.value === "") obj.value = T.title;
        translation = T.value;
      }
    }
    return translation;
  };
  //! Translate
  return (
    <>
      <Box sx={{ bgcolor: colors.paperTint, py: { xs: 5, md: 7 } }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 }, overflow: "hidden" }}>
        <Grid
          container
          spacing={5}
          alignItems="center"
        >
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={
                contentSettings.aboutUsImage
                  ? PF + "misc/" + contentSettings.aboutUsImage
                  : PF + "misc/about_us.jpeg"
              }
              alt="About Elifesaving Medicine"
              sx={{
                width: "100%",
                height: { xs: 220, md: 300 },
                objectFit: "cover",
                borderRadius: radii.lg,
                boxShadow: shadows.card,
                display: "block",
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={eyebrowSx}>About Us</Box>
            <Typography
              sx={{
                fontFamily: fonts.display,
                fontWeight: 800,
                fontSize: { xs: "1.6rem", md: "2rem" },
                color: colors.ink,
                mt: 2,
                lineHeight: 1.25,
              }}
            >
              {translateFront("About Us")}
            </Typography>
            <Typography
              sx={{
                fontSize: 15,
                color: colors.ink2,
                lineHeight: 1.7,
                mt: 2,
              }}
            >
              {contentSettings.aboutUs &&
                contentSettings.aboutUs.length > 0 &&
                contentSettings.aboutUs[0].tabDetails}
            </Typography>

            <Stack direction="row" spacing={3} alignItems="center" sx={{ mt: 3, flexWrap: "wrap", rowGap: 1.5 }}>
              {contentSettings.aboutUsVideos &&
                contentSettings.aboutUsVideos.length > 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<PlayArrow />}
                    onClick={() => setViewAboutVid(true)}
                    sx={{
                      borderColor: colors.primary,
                      color: colors.primary,
                      borderRadius: radii.md,
                      textTransform: "none",
                      fontWeight: 700,
                      borderWidth: "1.5px",
                      "&:hover": { borderColor: colors.primary, borderWidth: "1.5px", bgcolor: colors.primaryLight },
                    }}
                  >
                    {translateFront("Watch Overview")}
                  </Button>
                )}
              <Button
                endIcon={<ArrowForward />}
                onClick={() => setViewMoreAbout(true)}
                sx={{
                  color: colors.primary,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { color: colors.primaryDark, bgcolor: "transparent" },
                }}
              >
                {translateFront("Learn More")}
              </Button>
            </Stack>
          </Grid>
        </Grid>
        </Box>
      </Box>

      {viewMoreAbout && (
        <Dialog
          open={viewMoreAbout}
          onClose={() => setViewMoreAbout(false)}
          aria-labelledby={"view-more-about"}
        >
          <DialogTitle>{translateFront("About Us")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {contentSettings &&
                contentSettings.aboutUs &&
                contentSettings.aboutUs.length > 0 &&
                contentSettings.aboutUs.map((x, i) => (
                  <Accordion key={i}>
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      aria-label={`${i}Expand`}
                      aria-controls={`${i}-content`}
                      id={`${i}-header`}
                    >
                      <Typography>{x.tabTitle}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>{x.tabDetails}</AccordionDetails>
                  </Accordion>
                ))}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewMoreAbout(false)} color="error">
              {translateFront("Close")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {viewAboutVid && (
        <Dialog
          open={viewAboutVid}
          onClose={() => setViewAboutVid(false)}
          aria-labelledby={"about-us-video"}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: radii.lg,
              bgcolor: colors.ink,
              boxShadow: shadows.cardHover,
              overflow: "hidden",
            },
          }}
        >
          <IconButton
            onClick={() => setViewAboutVid(false)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              color: "#fff",
              bgcolor: "rgba(255,255,255,0.12)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
            }}
            size="small"
          >
            <Close fontSize="small" />
          </IconButton>
          <DialogContent sx={{ p: { xs: 1.5, sm: 3 } }}>
            <AboutUsVideo contentSettings={contentSettings} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
